import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Eye, AlertCircle, FileText, ChevronDown, MessageSquare } from 'lucide-react'
import ChatBlob from '../components/atomic/ChatBlob'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeSanitize from 'rehype-sanitize'
import markdownStyles from '../components/markdown-styles.module.css'
import { useTranslation } from '../i18n/LanguageContext'
import ChatZoomControls from '../components/sub-components/ChatZoomControls'
import { useChatZoom } from '../hooks/useChatZoom'
import { useThemeColor } from '../hooks/useThemeColor'
import { BASE_SHARES_URL } from '../components/constants'
import WidgetCard from '../components/sub-components/WidgetCard'
import { fetchWidgetCatalog } from '../slice/agentWidgetsSlice'
import PDFViewer from '../components/sub-components/PDFViwer'
import { clearSources } from '../slice/sourceSlice'
import { fetchPDF } from '../slice/pdfViewslice'
import Sidebar from '../components/sub-components/Sidebar'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'

const ANSWER_STATUS_COLORS = {
  accepted: '#10B981',
  review: '#F59E0B',
  missing: '#F97316',
  rejected: '#EF4444',
}

const SharedView = () => {
  const { shareToken } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const viewMode = location.state?.mode || 'full'
  const { t, language } = useTranslation()
  const { primaryColor } = useThemeColor()
  const dispatch = useDispatch()
  const { catalog: widgetCatalog } = useSelector(s => s.agentWidgets)

  const [loading, setLoading]       = useState(true)
  const [error, setError]           = useState(null)
  const [shareData, setShareData]   = useState(null)
  const [activeTab, setActiveTab]   = useState(null)
  const [expandedQuestionId, setExpandedQuestionId] = useState(null)
  const [showAllActivities, setShowAllActivities]   = useState(false)
  const [isSidebarOpen, setIsSidebarOpen]           = useState(true)
  const [sidebarWidth, setSidebarWidth]             = useState(445)
  const [questionsPage, setQuestionsPage] = useState(1)
  const questionsPerPage = 10
  const chatContainerRef = useRef(null)
  const chatZoom = useChatZoom(chatContainerRef)
  const fetchedPdfRef    = useRef(null)

  const sourcePdf = useSelector(s => s.source)

  const [navWidth, setNavWidth] = useState(() => {
    try { return localStorage.getItem('navCollapsed') === '1' ? 72 : 280 } catch { return 280 }
  })
  useEffect(() => {
    const handler = (e) => setNavWidth(e.detail.isCollapsed ? 72 : 280)
    window.addEventListener('sidebarToggle', handler)
    return () => window.removeEventListener('sidebarToggle', handler)
  }, [])

  useEffect(() => {
    if (!widgetCatalog?.length) dispatch(fetchWidgetCatalog())
  }, [widgetCatalog, dispatch])

  // Fetch PDF with owner credentials when ChatBlob dispatches addSource
  useEffect(() => {
    const filename = sourcePdf?.currentPdf?.filename
    if (!filename || sourcePdf?.currentPdf?.blob || !sourcePdf?.loading) return
    if (filename === fetchedPdfRef.current) return
    const ownerUserId = shareData?.share?.shared_by_user_id
    const ownerTaskId = shareData?.task_id
    const historyId   = shareData?.share?.history_id
    if (!ownerUserId || !ownerTaskId || !historyId) return
    fetchedPdfRef.current = filename
    dispatch(fetchPDF({ userId: ownerUserId, taskId: ownerTaskId, filename, historyId, itemId: sourcePdf?.currentPdf?.itemId }))
      .finally(() => { fetchedPdfRef.current = null })
  }, [sourcePdf?.currentPdf?.filename, sourcePdf?.currentPdf?.blob, sourcePdf?.loading, shareData, dispatch])

  useEffect(() => {
    if (!sourcePdf?.currentPdf?.filename) fetchedPdfRef.current = null
  }, [sourcePdf?.currentPdf?.filename])

  // Clear PDF state when leaving chat tab or on unmount
  useEffect(() => {
    if (viewMode === 'chat') return
    if (activeTab !== 'chat') dispatch(clearSources())
  }, [activeTab, viewMode, dispatch])

  useEffect(() => {
    return () => { dispatch(clearSources()) }
  }, [dispatch])

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const authToken = localStorage.getItem('token')
      const res = await fetch(`${BASE_SHARES_URL}/view/${shareToken}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      if (!res.ok) {
        if (res.status === 403 || res.status === 401) {
          toast.error(t('share.accessRevoked'))
          navigate('/notifications', { replace: true })
          return
        }
        if (res.status === 404) setError(t('share.notFound'))
        else setError(t('share.loadingError'))
        return
      }
      const data = await res.json()
      setShareData(data)
    } catch {
      setError(t('share.loadingError'))
    } finally {
      setLoading(false)
    }
  }, [shareToken, t])

  useEffect(() => { fetchData() }, [fetchData])

  // Mark notifications for this share as read when the view opens
  useEffect(() => {
    const token = localStorage.getItem('token')
    if (!token || !shareToken) return
    fetch(`${BASE_SHARES_URL}/notifications/mark-by-link`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ action_link: `/shared/${shareToken}` }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.unread_count !== undefined) {
          window.dispatchEvent(new CustomEvent('notifCountChange', { detail: { count: data.unread_count } }))
        }
      })
      .catch(() => {})
  }, [shareToken])

  // If agent has disabled Questions (show_project_detail=false) and user arrived
  // in full/overview mode, redirect directly to chat — no overview to show
  useEffect(() => {
    if (viewMode !== 'full') return
    if (!shareData) return
    if (shareData.show_project_detail === false || shareData.overview_locked) {
      navigate(`/shared/${shareToken}`, { state: { mode: 'chat' }, replace: true })
    }
  }, [shareData, viewMode, shareToken, navigate])

  // Set default tab after data loads (only needed in 'full' mode)
  useEffect(() => {
    if (viewMode !== 'full') return
    if (!shareData || activeTab !== null) return
    setActiveTab(shareData.is_overview_enabled ? 'overview' : 'documents')
  }, [shareData, activeTab, viewMode])

  // Scroll chat to bottom when entering chat tab
  useEffect(() => {
    if (activeTab === 'chat' && chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
    }
  }, [activeTab])

  // ── loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div
        className="fixed top-0 right-0 bottom-0 flex items-center justify-center bg-white"
        style={{ left: `${navWidth}px` }}
      >
        <svg className="animate-spin h-8 w-8" viewBox="0 0 24 24" style={{ color: primaryColor }}>
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    )
  }

  // ── error ─────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div
        className="fixed top-0 right-0 bottom-0 flex flex-col items-center justify-center bg-white gap-4 text-center px-6"
        style={{ left: `${navWidth}px` }}
      >
        <AlertCircle size={40} className="text-[#D1D5DB]" />
        <p className="text-[16px] font-semibold text-[#111827]">{error}</p>
        <button
          onClick={() => navigate('/history?tab=shared')}
          className="text-[13px] font-medium px-4 py-2 rounded-lg border border-[#E5E7EB] text-[#374151] hover:bg-[#F9FAFB] transition-colors"
        >
          {t('history.tabInvitedProjects')}
        </button>
      </div>
    )
  }

  const {
    share,
    task_id: shareTaskId = null,
    task_name: shareTaskName = null,
    qa_history = [],
    files = [],
    questions = [],
    widget_layout = [],
    widget_data = {},
    is_overview_enabled = false,
    show_project_detail = true,
    show_files_tab = true,
    allow_local_files = true,
    derived = {},
    answer_statuses = [],
  } = shareData || {}

  const projectName = share?.project_name || t('analysis.projectFallback')
  const sharedBy    = share?.shared_by_email || ''

  // Filter files: if local uploads disabled, only show SharePoint files
  const visibleFiles = allow_local_files
    ? files
    : files.filter(f => f.source_type === 'sharepoint')

  const tabs = [
    ...(is_overview_enabled ? [{ id: 'overview',   label: t('projectDetail.tabOverview') }]   : []),
    ...(show_files_tab       ? [{ id: 'documents',  label: t('projectDetail.tabDocuments') }]  : []),
    ...(show_project_detail  ? [{ id: 'questions',  label: t('projectDetail.tabQuestions') }]  : []),
    { id: 'activities', label: t('projectDetail.tabActivities') },
  ]

  // Widget helpers
  const catalogByKey = (widgetCatalog || []).reduce((m, c) => { m[c.key] = c; return m; }, {})
  const enabledWidgets = widget_layout
    .filter(l => l.is_enabled && catalogByKey[l.widget_key])
    .sort((a, b) => a.position - b.position)

  // Compute derived — zeroed out when standard questions are disabled for this agent
  const validSqIds = new Set((show_project_detail ? (questions || []) : []).map(q => parseInt(q.id)))
  const scopedStatuses = (answer_statuses || []).filter(
    s => validSqIds.has(parseInt(s.sq_id))
  )
  const answerCounts = scopedStatuses.reduce(
    (acc, s) => {
      if (s.answer_status && Object.prototype.hasOwnProperty.call(acc, s.answer_status)) {
        acc[s.answer_status] += 1
      }
      return acc
    },
    { accepted: 0, review: 0, missing: 0, rejected: 0 }
  )
  const answeredTotal = answerCounts.accepted + answerCounts.review + answerCounts.missing + answerCounts.rejected
  const computedDerived = {
    ...derived,
    checklist: {
      accepted:      answerCounts.accepted,
      further_review: answerCounts.review,
      info_missing:  answerCounts.missing,
      rejected:      answerCounts.rejected,
      pending:       Math.max(0, (show_project_detail ? (questions?.length || 0) : 0) - answeredTotal),
    },
    analysis_score: {
      accepted:   answerCounts.accepted,
      total:      answeredTotal,
      percentage: answeredTotal ? Math.round(100 * answerCounts.accepted / answeredTotal) : 0,
    },
  }

  // Questions helpers
  const titleToAnswer = {}
  qa_history.forEach(msg => {
    if (msg?.role !== 'user' && msg?.title && msg?.content) {
      titleToAnswer[String(msg.title).toLowerCase().trim()] = msg.content
    }
  })

  // Activities helpers
  const getDateLabel = (dateString) => {
    const date = new Date(dateString)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    if (date.toDateString() === today.toDateString()) return t('projectDetail.activityTodayLabel')
    if (date.toDateString() === yesterday.toDateString()) return t('projectDetail.activityYesterdayLabel')
    return date.toLocaleDateString(language, { day: 'numeric', month: 'long', year: 'numeric' })
  }

  const getActivityIcon = (type) => {
    switch (type) {
      case 'file_uploaded':
        return <div className="w-2 h-2 rounded-full bg-[#212121] mt-2" />
      case 'analysis_performed':
        return <div className="w-6 h-6 rounded-full bg-[#F3F4F6] flex items-center justify-center"><span className="text-[12px] font-medium text-[#6B7280]">A</span></div>
      case 'question_asked':
        return <div className="w-6 h-6 rounded-full bg-[#F3F4F6] flex items-center justify-center"><span className="text-[12px] font-medium text-[#6B7280]">Q</span></div>
      default:
        return <div className="w-6 h-6 rounded-full bg-[#F3F4F6] flex items-center justify-center"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4"/></svg></div>
    }
  }

  const allActivities = (() => {
    const activities = []
    files.forEach(file => {
      activities.push({
        id: `file-${file.id || file.filename}`,
        type: 'file_uploaded',
        title: t('projectDetail.activityFileUploaded'),
        description: file.filename,
        status: file.is_processed ? t('projectDetail.activityFileProcessed') : null,
        date: file.created_at || new Date().toISOString(),
        user: { name: sharedBy || t('projectDetail.activityDefaultUser'), avatar: null },
      })
    })
    qa_history.forEach(msg => {
      if (msg.role === 'user') {
        activities.push({
          id: `question-${msg.id}`,
          type: 'question_asked',
          title: t('projectDetail.activityQuestionAsked'),
          description: (msg.content?.substring(0, 80) || '') + (msg.content?.length > 80 ? '...' : ''),
          date: msg.timestamp || new Date().toISOString(),
          user: { name: sharedBy || t('projectDetail.activityDefaultUser'), avatar: null },
        })
      } else if (msg.role === 'assistant') {
        activities.push({
          id: `analysis-${msg.id}`,
          type: 'analysis_performed',
          title: t('projectDetail.activityAnalysisPerformed'),
          description: (msg.content?.substring(0, 80) || '') + (msg.content?.length > 80 ? '...' : ''),
          date: msg.timestamp || new Date().toISOString(),
          user: { name: t('projectDetail.activityAiAgent'), avatar: null, isAI: true },
        })
      }
    })
    activities.sort((a, b) => new Date(b.date) - new Date(a.date))
    return activities
  })()

  const initialActivitiesCount = 5
  const displayedActivities = showAllActivities ? allActivities : allActivities.slice(0, initialActivitiesCount)
  const groupedActivities = displayedActivities.reduce((acc, act) => {
    const label = getDateLabel(act.date)
    if (!acc[label]) acc[label] = []
    acc[label].push(act)
    return acc
  }, {})

  const getInitials = (name) => name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)

  // Chat mode → full Analysis-style layout
  if (viewMode === 'chat') {
    return (
      <div
        className="fixed top-0 right-0 bottom-0 flex bg-white transition-all duration-300"
        style={{ left: `${navWidth}px` }}
      >
        {/* Main content wrapper */}
        <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
          {/* Header */}
          <header className="flex items-center justify-between px-6 min-h-[65px] border-b border-[#E5E7EB] flex-shrink-0">
            {/* Breadcrumb: Projects / Invited Projects / Project Name */}
            <div className="flex items-center gap-2 text-[14px] min-w-0">
              <button onClick={() => navigate('/history')} className="text-[#6B7280] hover:text-[#374151] flex-shrink-0">
                {t('analysis.breadcrumbProjects')}
              </button>
              <span className="text-[#9CA3AF] flex-shrink-0">/</span>
              <button
                onClick={() => navigate('/history?tab=shared')}
                className="text-[#6B7280] hover:text-[#374151] flex-shrink-0 whitespace-nowrap"
              >
                {t('history.tabInvitedProjects')}
              </button>
              <span className="text-[#9CA3AF] flex-shrink-0">/</span>
              <span className="text-[#111827] font-medium truncate">{projectName}</span>
            </div>

            {/* Right: shared-by email + read-only badge + sidebar toggle */}
            <div className="flex items-center gap-3 flex-shrink-0 ml-4">
              {sharedBy && (
                <span className="text-[12px] text-[#9CA3AF] hidden md:block truncate max-w-[200px]">
                  {t('share.sharedBy')}: <span className="text-[#6B7280]">{sharedBy}</span>
                </span>
              )}
              <div
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border flex-shrink-0"
                style={{ borderColor: primaryColor, color: primaryColor }}
              >
                <Eye size={13} />
                <span className="text-[12px] font-medium">{t('share.readOnlyBadge')}</span>
              </div>
              {!isSidebarOpen && (
                <button
                  onClick={() => setIsSidebarOpen(true)}
                  className="p-2 text-[#6B7280] hover:text-[#374151] hover:bg-[#F3F4F6] rounded-lg transition-colors"
                  title={t('sidebar.openSidebar')}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="3" y="4" width="18" height="16" rx="2" ry="2" />
                    <line x1="15" y1="4" x2="15" y2="20" />
                  </svg>
                </button>
              )}
            </div>
          </header>

          {/* Chat messages */}
          {/* Padding lives on the inner wrapper, not this scroll ancestor —
              padding-top here would create a permanent gap above any
              `position: sticky` child (the Standard Question pill), since a
              scroll container's own padding never scrolls away. */}
          <div ref={chatContainerRef} className="flex-1 overflow-y-auto px-6 thin-scrollbar">
            <div className="max-w-4xl mx-auto flex flex-col gap-6 py-6" style={{ zoom: chatZoom.zoom }}>
              {qa_history.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-[#9CA3AF]">
                  <p className="text-[14px]">{t('notifications.empty') || 'Keine Nachrichten'}</p>
                </div>
              ) : (
                qa_history.map((message, index, arr) => {
                  // Standard Question detection (matches Analysis.jsx) — the
                  // sticky pill inside ChatBlob only shows for these.
                  let isSidebarQuestion = false;
                  let pairedTitle = '';
                  if (message.title && String(message.title).trim()) {
                    isSidebarQuestion = true;
                    pairedTitle = message.title;
                  } else if (message.role !== 'user') {
                    for (let i = index - 1; i >= 0; i--) {
                      if (arr[i].role === 'user') {
                        if (arr[i].title && String(arr[i].title).trim()) {
                          isSidebarQuestion = true;
                          pairedTitle = arr[i].title;
                        }
                        break;
                      }
                    }
                  }
                  return (
                  <ChatBlob
                    key={index}
                    user={message.role}
                    message={{
                      id: message.id,
                      content: message.content || '',
                      source: message.sources || [],
                      title: message.title || '',
                      comment: null,
                      analysis: message.analysis_meta || null,
                    }}
                    historyId={share?.history_id}
                    taskId={null}
                    openSidebar={() => {}}
                    readOnly={true}
                    isAgentQuestion={isSidebarQuestion}
                    agentQuestionTitle={pairedTitle}
                  />
                  );
                })
              )}
            </div>
          </div>

          <ChatZoomControls {...chatZoom} />
          {/* Disabled input — same style as Analysis ChatInput area */}
          <div className="pb-6 px-6 flex-shrink-0">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] cursor-not-allowed select-none opacity-60">
                <span className="text-[14px] text-[#9CA3AF] flex-1">{t('share.readOnlyHint')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right sidebar — same as Analysis */}
        <div
          className={`h-full flex-shrink-0 transition-all duration-300 overflow-hidden bg-white ${isSidebarOpen ? 'border-l border-[#E5E7EB]' : 'hidden'}`}
          style={{ width: isSidebarOpen ? `${sidebarWidth}px` : '0' }}
        >
          <Sidebar
            taskID={shareTaskId}
            historyId={share?.history_id || ''}
            onClose={() => setIsSidebarOpen(false)}
            isOpen={isSidebarOpen}
            onWidthChange={(w) => setSidebarWidth(w)}
            readOnly={true}
            ownerUserId={share?.shared_by_user_id || null}
            sqCategoriesEnabled={!!shareData?.sq_categories_in_sidebar}
          />
        </div>
      </div>
    )
  }

  return (
    <div
      className="fixed top-0 right-0 bottom-0 flex flex-col bg-[#F9FAFB] overflow-hidden"
      style={{ left: `${navWidth}px` }}
    >
      {/* ── Header ────────────────────────────────────────────────────────── */}
      <header className="flex items-center justify-between px-6 min-h-[65px] border-b border-[#E5E7EB] bg-white flex-shrink-0">
        <div className="flex items-center gap-2 text-[14px] min-w-0">
          <button onClick={() => navigate('/history')} className="text-[#6B7280] hover:text-[#374151] flex-shrink-0">
            {t('analysis.breadcrumbProjects')}
          </button>
          <span className="text-[#9CA3AF] flex-shrink-0">/</span>
          <button onClick={() => navigate('/history?tab=shared')} className="text-[#6B7280] hover:text-[#374151] flex-shrink-0 whitespace-nowrap">
            {t('history.tabInvitedProjects')}
          </button>
          <span className="text-[#9CA3AF] flex-shrink-0">/</span>
          <span className="text-[#111827] font-medium truncate max-w-[260px]">{projectName}</span>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          {shareTaskName && (
            <span className="text-[12px] text-[#9CA3AF] hidden md:block">
              {t('projectDetail.agentLabel', { name: shareTaskName })}
            </span>
          )}
          {sharedBy && (
            <span className="text-[12px] text-[#9CA3AF] hidden md:block">
              {t('share.sharedBy')}: <span className="text-[#6B7280]">{sharedBy}</span>
            </span>
          )}
          <div
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border"
            style={{ borderColor: primaryColor, color: primaryColor }}
          >
            <Eye size={13} />
            <span className="text-[12px] font-medium">{t('share.readOnlyBadge')}</span>
          </div>
          <button
            onClick={() => navigate(`/shared/${shareToken}`, { state: { mode: 'chat' } })}
            className="flex items-center gap-2 px-4 py-2 text-[13px] font-semibold text-white rounded-lg hover:opacity-90 transition-colors flex-shrink-0"
            style={{ backgroundColor: primaryColor }}
          >
            <MessageSquare size={14} />
            {t('history.continue')}
          </button>
        </div>
      </header>


      {/* ── Tab bar (full mode only) ─────────────────────────────────────── */}
      {viewMode === 'full' && (
        <div className="border-b border-[#E5E7EB] bg-white px-6 flex-shrink-0">
          <div className="flex items-center gap-6">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="pb-3 pt-3 text-[14px] font-medium transition-colors relative"
                style={{ color: activeTab === tab.id ? primaryColor : '#9E9E9E' }}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-[2px]" style={{ backgroundColor: primaryColor }} />
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Tab content ───────────────────────────────────────────────────── */}
      <div className={`flex-1 ${(viewMode === 'chat' || activeTab === 'chat') ? 'overflow-hidden' : 'overflow-y-auto thin-scrollbar'}`}>

        {/* Overview tab */}
        {activeTab === 'overview' && (
          <div className="p-6">
            {enabledWidgets.length === 0 ? (
              <div className="col-span-3 text-center text-[14px] text-[#9CA3AF] py-12 border-2 border-dashed border-[#E5E7EB] rounded-xl">
                {t('agentSettings.overviewEmpty')}
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-4">
                {enabledWidgets.map(entry => (
                  <WidgetCard
                    key={entry.widget_key}
                    catalogItem={catalogByKey[entry.widget_key]}
                    data={widget_data[entry.widget_key] || {}}
                    derived={computedDerived}
                    aispaceColor={primaryColor}
                    mode="live"
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Documents tab */}
        {activeTab === 'documents' && (
          <div className="p-6 max-w-4xl mx-auto">
            {visibleFiles.length === 0 ? (
              <p className="text-[13px] text-[#9CA3AF] text-center py-12">{t('sidebar.noFilesUploaded')}</p>
            ) : (
              <div className="flex flex-col gap-2">
                {visibleFiles.map((file, i) => (
                  <div key={i} className="flex items-start gap-3 px-4 py-3 rounded-lg bg-white border border-[#E5E7EB]">
                    <FileText size={16} className="text-[#9CA3AF] flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] text-[#374151] truncate">{file.filename || file.name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        {file.source_type === 'sharepoint' && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#EFF6FF] text-[#2563EB]">SharePoint</span>
                        )}
                        {file.folder_name && (
                          <span className="text-[11px] text-[#9CA3AF] truncate">{file.folder_name}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Questions tab */}
        {activeTab === 'questions' && (
          <div className="border border-[#EEEEEE] rounded-lg overflow-hidden bg-white overflow-x-auto">
            <div className="min-w-[780px]">
              <div className="flex items-center px-6 py-3 bg-[#F5F5F5]">
                <div className="flex-1 min-w-0 text-[13px] font-medium text-[#212121] pr-4">{t('projectDetail.questionsCategory')}</div>
                <div className="flex-[1.5] min-w-0 text-[13px] font-medium text-[#212121] pr-4">{t('projectDetail.questionsTitle')}</div>
                <div className="flex-[1.5] min-w-0 text-[13px] font-medium text-[#212121] pr-4">{t('projectDetail.questionsDescription')}</div>
                <div className="flex-[2.5] min-w-0 text-[13px] font-medium text-[#212121] pr-4">{t('projectDetail.questionsAnswer')}</div>
                <div className="w-[210px] flex-shrink-0 text-[13px] font-medium text-[#212121] text-center">{t('projectDetail.questionsStatus')}</div>
              </div>

              <div>
                {questions.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 border-t border-[#EEEEEE]">
                    <div className="relative mb-5">
                      <div className="w-[100px] h-[100px] rounded-full border-2 border-dashed border-[#E5E7EB] flex items-center justify-center relative">
                        <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#E5E7EB]" />
                        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#E5E7EB]" />
                        <div className="absolute top-1/2 -left-1 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#E5E7EB]" />
                        <div className="absolute top-1/2 -right-1 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#E5E7EB]" />
                        <div className="w-14 h-14 rounded-full bg-[#F9FAFB] border border-[#E5E7EB] flex items-center justify-center">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                          </svg>
                        </div>
                      </div>
                    </div>
                    <h3 className="text-[15px] font-semibold text-[#111827] mb-1.5">{t('projectDetail.noQuestionsAssigned')}</h3>
                    <p className="text-[13px] text-[#6B7280] text-center max-w-[300px]">{t('projectDetail.noQuestionsHint')}</p>
                  </div>
                ) : (
                  questions.slice((questionsPage - 1) * questionsPerPage, questionsPage * questionsPerPage).map((question, index) => {
                    const questionStatus = answer_statuses.find(s => parseInt(s.sq_id) === parseInt(question.id))
                    const aiAnswer = question.title ? titleToAnswer[String(question.title).toLowerCase().trim()] : null
                    const currentAnswerStatus = questionStatus?.answer_status || null
                    const statusColor = currentAnswerStatus ? ANSWER_STATUS_COLORS[currentAnswerStatus] : null
                    const isExpanded = expandedQuestionId === question.id

                    return (
                      <div key={question.id || index} className="border-t border-[#EEEEEE]">
                        <div
                          className="flex items-start px-6 py-5 cursor-pointer hover:bg-[#FAFAFA] min-h-[88px]"
                          onClick={() => setExpandedQuestionId(isExpanded ? null : question.id)}
                        >
                          <div className="flex-1 min-w-0 text-[14px] text-[#616161] pr-4">
                            <span className="line-clamp-2">{question.category || t('projectDetail.defaultCategory')}</span>
                          </div>
                          <div className="flex-[1.5] min-w-0 text-[14px] text-[#616161] pr-4">
                            <span className="line-clamp-2">{question.title || '-'}</span>
                          </div>
                          <div className="flex-[1.5] min-w-0 text-[14px] text-[#616161] pr-4">
                            <span className="line-clamp-2">{question.description || '-'}</span>
                          </div>
                          <div className="flex-[2.5] min-w-0 text-[14px] text-[#616161] pr-4">
                            {aiAnswer ? (
                              <div className="line-clamp-2 [&_p]:inline [&_strong]:font-semibold [&_em]:italic">
                                <ReactMarkdown
                                  remarkPlugins={[remarkGfm]}
                                  rehypePlugins={[rehypeSanitize]}
                                  components={{
                                    p: ({ node, ...props }) => <span {...props} />,
                                    h1: ({ node, ...props }) => <span {...props} />,
                                    h2: ({ node, ...props }) => <span {...props} />,
                                    h3: ({ node, ...props }) => <span {...props} />,
                                    ul: ({ node, ...props }) => <span {...props} />,
                                    ol: ({ node, ...props }) => <span {...props} />,
                                    li: ({ node, children }) => <span>{children} </span>,
                                    br: () => <span> </span>,
                                    pre: ({ node, ...props }) => <span {...props} />,
                                    code: ({ node, ...props }) => <code className="text-[13px]" {...props} />,
                                    a: ({ node, ...props }) => <span className="underline" {...props} />,
                                    table: () => null,
                                  }}
                                >
                                  {aiAnswer}
                                </ReactMarkdown>
                              </div>
                            ) : (
                              <span className="line-clamp-2">-</span>
                            )}
                          </div>
                          <div className="w-[210px] flex-shrink-0 flex items-center justify-center gap-2" onClick={e => e.stopPropagation()}>
                            <div
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium"
                              style={{
                                width: 170, minWidth: 170, maxWidth: 170, boxSizing: 'border-box',
                                ...(statusColor
                                  ? { backgroundColor: statusColor, color: '#ffffff', border: `1px solid ${statusColor}` }
                                  : { backgroundColor: '#ffffff', color: '#6B7280', border: '1px solid #E5E7EB' }),
                              }}
                            >
                              {!statusColor && (
                                <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: '#D1D5DB' }} />
                              )}
                              <span className="flex-1 min-w-0 truncate text-center">
                                {currentAnswerStatus ? t(`answerStatus.${currentAnswerStatus}`) : t('projectDetail.openAction')}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => setExpandedQuestionId(isExpanded ? null : question.id)}
                              className="p-1 rounded text-[#9CA3AF] hover:text-[#374151] hover:bg-[#F3F4F6]"
                            >
                              <ChevronDown size={16} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                            </button>
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="px-6 pb-6 bg-[#FAFAFA]">
                            <div className="max-h-[480px] overflow-y-auto thin-scrollbar pr-2 space-y-4">
                              <div>
                                <div className="text-[11px] uppercase tracking-wide text-[#9CA3AF] font-semibold mb-1">{t('projectDetail.questionsCategory')}</div>
                                <div className="text-[14px] text-[#374151]">{question.category || t('projectDetail.defaultCategory')}</div>
                              </div>
                              <div>
                                <div className="text-[11px] uppercase tracking-wide text-[#9CA3AF] font-semibold mb-1">{t('projectDetail.questionsTitle')}</div>
                                <div className="text-[14px] text-[#212121] font-medium">{question.title || '-'}</div>
                              </div>
                              <div>
                                <div className="text-[11px] uppercase tracking-wide text-[#9CA3AF] font-semibold mb-1">{t('projectDetail.questionsDescription')}</div>
                                <div className="text-[14px] text-[#374151] whitespace-pre-wrap">{question.description || '-'}</div>
                              </div>
                              <div>
                                <div className="text-[11px] uppercase tracking-wide text-[#9CA3AF] font-semibold mb-1">{t('projectDetail.questionsAnswer')}</div>
                                {aiAnswer ? (
                                  <div className={`text-[14px] text-[#374151] ${markdownStyles.reactMarkDown}`}>
                                    <ReactMarkdown
                                      remarkPlugins={[remarkGfm]}
                                      rehypePlugins={[rehypeSanitize]}
                                      components={{
                                        p: ({ node, ...props }) => {
                                          const isInListItem = node?.parent && node.parent.tagName === 'li'
                                          return <p style={isInListItem ? { fontWeight: 'normal', fontSize: '14px' } : { fontSize: '14px' }} {...props} />
                                        },
                                        li: ({ node, ...props }) => <li style={{ fontWeight: 'normal', fontSize: '14px' }} {...props} />,
                                        table: ({ node, ...props }) => <table className="border-collapse border border-gray-300 my-3 text-[14px]" {...props} />,
                                        thead: ({ node, ...props }) => <thead className="bg-gray-100" {...props} />,
                                        tr: ({ node, ...props }) => <tr className="border-b border-gray-300" {...props} />,
                                        th: ({ node, ...props }) => <th className="border border-gray-300 px-3 py-1.5 text-left font-semibold text-[14px]" {...props} />,
                                        td: ({ node, ...props }) => <td className="border border-gray-300 px-3 py-1.5 text-[14px]" {...props} />,
                                      }}
                                    >
                                      {aiAnswer}
                                    </ReactMarkdown>
                                  </div>
                                ) : (
                                  <div className="text-[14px] italic text-[#9CA3AF]">-</div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })
                )}
              </div>

              {questions.length > questionsPerPage && (
                <div className="flex items-center justify-between px-6 py-3 border-t border-[#EEEEEE]">
                  <span className="text-[14px] text-[#6B7280] font-medium">
                    {t('pagination.pageOf', { current: questionsPage, total: Math.ceil(questions.length / questionsPerPage) || 1 })}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setQuestionsPage(p => Math.max(1, p - 1))}
                      disabled={questionsPage === 1}
                      className="px-4 py-1.5 text-[14px] font-semibold text-[#616161] border border-[#EEEEEE] rounded-md hover:bg-[#F5F5F5] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('common.previous')}
                    </button>
                    <button
                      onClick={() => setQuestionsPage(p => Math.min(Math.ceil(questions.length / questionsPerPage), p + 1))}
                      disabled={questionsPage >= Math.ceil(questions.length / questionsPerPage)}
                      className="px-4 py-1.5 text-[14px] font-semibold text-[#616161] border border-[#EEEEEE] rounded-md hover:bg-[#F5F5F5] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('common.next')}
                    </button>
                  </div>
                </div>
              )}
            </div>
            </div>
        )}

        {/* Activities tab */}
        {activeTab === 'activities' && (
          <div className="p-6 max-w-4xl mx-auto">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E5E7EB]">
              {allActivities.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="relative mb-5">
                    <div className="w-[100px] h-[100px] rounded-full border-2 border-dashed border-[#E5E7EB] flex items-center justify-center relative">
                      <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#E5E7EB]" />
                      <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#E5E7EB]" />
                      <div className="absolute top-1/2 -left-1 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#E5E7EB]" />
                      <div className="absolute top-1/2 -right-1 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#E5E7EB]" />
                      <div className="w-14 h-14 rounded-full bg-[#F9FAFB] border border-[#E5E7EB] flex items-center justify-center">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                  <h3 className="text-[15px] font-semibold text-[#111827] mb-1.5">{t('projectDetail.activitiesEmptyTitle')}</h3>
                  <p className="text-[13px] text-[#6B7280] text-center max-w-[340px]">{t('projectDetail.activitiesEmpty')}</p>
                </div>
              ) : (
                <>
                  {Object.entries(groupedActivities).map(([dateLabel, acts]) => (
                    <div key={dateLabel} className="mb-8 last:mb-0">
                      <p className="text-[12px] font-semibold text-[#6B7280] mb-4">{dateLabel}</p>
                      {acts.map((activity, index) => (
                        <div key={activity.id} className="flex gap-3">
                          <div className="flex flex-col items-center">
                            {getActivityIcon(activity.type)}
                            {index < acts.length - 1 && (
                              <div className="w-[1px] bg-[#E5E7EB] flex-1 min-h-[60px] mt-2" />
                            )}
                          </div>
                          <div className={`flex-1 ${index < acts.length - 1 ? 'pb-6' : ''}`}>
                            <p className="text-[16px] font-semibold text-[#212121]">{activity.title}</p>
                            <p className="text-[14px] text-[#616161] mt-1">
                              {activity.description}
                              {activity.status && ` — ${activity.status}`}
                            </p>
                            {activity.user && (
                              <div className="flex items-center gap-2 mt-2">
                                <div
                                  className="w-6 h-6 rounded-full flex items-center justify-center"
                                  style={{ backgroundColor: activity.user.isAI ? primaryColor : '#F3F4F6' }}
                                >
                                  <span className={`text-[10px] font-semibold ${activity.user.isAI ? 'text-white' : 'text-[#6B7280]'}`}>
                                    {getInitials(activity.user.name)}
                                  </span>
                                </div>
                                <span className="text-[14px] font-semibold text-[#212121]">{activity.user.name}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                  {allActivities.length > initialActivitiesCount && (
                    <button
                      onClick={() => setShowAllActivities(!showAllActivities)}
                      className="flex items-center gap-1 text-[14px] font-medium hover:opacity-80 transition-opacity"
                      style={{ color: primaryColor }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                        className={`transition-transform ${showAllActivities ? 'rotate-180' : ''}`}>
                        <polyline points="6 9 12 15 18 9"/>
                      </svg>
                      {showAllActivities ? t('projectDetail.activityShowLess') : t('projectDetail.activityShowOlder')}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Chat tab (full mode only — viewMode==='chat' is handled by the early return above) */}
        {activeTab === 'chat' && (
          <div className="flex h-full" style={{ height: 'calc(100vh - 65px - 38px - 49px)' }}>

            {/* Chat messages + input */}
            <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
              {/* Padding on the inner wrapper, not here — see the other
                  chatContainerRef above for why. */}
              <div ref={chatContainerRef} className="flex-1 overflow-y-auto px-6 thin-scrollbar">
                <div className="max-w-4xl mx-auto flex flex-col gap-6 py-6">
                  {qa_history.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-[#9CA3AF]">
                      <p className="text-[14px]">{t('notifications.empty') || 'Keine Nachrichten'}</p>
                    </div>
                  ) : (
                    qa_history.map((message, index, arr) => {
                      // Standard Question detection (matches Analysis.jsx) —
                      // the sticky pill inside ChatBlob only shows for these.
                      let isSidebarQuestion = false;
                      let pairedTitle = '';
                      if (message.title && String(message.title).trim()) {
                        isSidebarQuestion = true;
                        pairedTitle = message.title;
                      } else if (message.role !== 'user') {
                        for (let i = index - 1; i >= 0; i--) {
                          if (arr[i].role === 'user') {
                            if (arr[i].title && String(arr[i].title).trim()) {
                              isSidebarQuestion = true;
                              pairedTitle = arr[i].title;
                            }
                            break;
                          }
                        }
                      }
                      return (
                      <ChatBlob
                        key={index}
                        user={message.role}
                        message={{
                          id: message.id,
                          content: message.content || '',
                          source: message.sources || [],
                          title: message.title || '',
                          comment: null,
                          analysis: message.analysis_meta || null,
                        }}
                        historyId={share?.history_id}
                        taskId={null}
                        openSidebar={() => {}}
                        readOnly={true}
                        isAgentQuestion={isSidebarQuestion}
                        agentQuestionTitle={pairedTitle}
                      />
                      );
                    })
                  )}
                </div>
              </div>

              {/* Disabled input */}
              <div className="pb-6 px-6 flex-shrink-0">
                <div className="max-w-4xl mx-auto">
                  <div
                    className="flex items-center gap-3 px-4 py-3 rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] cursor-not-allowed select-none"
                    title={t('share.readOnlyHint')}
                  >
                    <span className="text-[14px] text-[#D1D5DB] flex-1">{t('share.readOnlyHint')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right sidebar — collapsible */}
            <div className={`${isSidebarOpen ? 'w-[360px]' : 'w-0'} flex-shrink-0 border-l border-[#E5E7EB] bg-white flex flex-col overflow-hidden transition-all duration-300`}>
              <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E7EB] flex-shrink-0">
                <span className="text-[14px] font-semibold text-[#111827]">
                  {t('sidebar.dateien')} ({visibleFiles.length})
                </span>
                {(sourcePdf?.currentPdf?.blob || (sourcePdf?.currentPdf?.filename && sourcePdf?.error && !sourcePdf?.loading)) && (
                  <button
                    onClick={() => { dispatch(clearSources()); fetchedPdfRef.current = null }}
                    className="text-[12px] text-[#6B7280] hover:text-[#374151] underline transition-colors"
                  >
                    {t('sidebar.backToFiles')}
                  </button>
                )}
              </div>

              <div className="flex-1 overflow-hidden">
                {sourcePdf?.loading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="flex flex-col items-center gap-3">
                      <svg className="animate-spin h-8 w-8" viewBox="0 0 24 24" style={{ color: primaryColor }}>
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      <p className="text-[13px] text-[#9CA3AF]">{t('sidebar.pdfLoading')}</p>
                    </div>
                  </div>
                ) : sourcePdf?.currentPdf?.blob ? (
                  <PDFViewer
                    file={sourcePdf.currentPdf.blob}
                    initialPage={sourcePdf.currentPdf.page || 1}
                  />
                ) : sourcePdf?.currentPdf?.filename && sourcePdf?.error ? (
                  <div className="flex items-center justify-center h-full p-6 text-center">
                    <p className="text-[13px] text-[#6B7280]">
                      {sourcePdf.errorCode === 'sharepoint_file_stale'
                        ? t('sidebar.sourceFileStale')
                        : sourcePdf.errorCode === 'file_decryption_failed'
                        ? t('sidebar.sourceFileDecryptionFailed')
                        : sourcePdf.errorCode === 'timeout'
                        ? t('sidebar.sourceFileTimeout')
                        : sourcePdf.errorCode === 'load_failed'
                        ? t('sidebar.sourceFileLoadFailed')
                        : t('sidebar.sourceFileNotFound')}
                    </p>
                  </div>
                ) : (
                  <div className="p-4 overflow-y-auto h-full thin-scrollbar flex flex-col gap-2">
                    {visibleFiles.length === 0 ? (
                      <p className="text-[13px] text-[#9CA3AF] text-center py-8">{t('sidebar.noFilesUploaded')}</p>
                    ) : (
                      visibleFiles.map((file, i) => (
                        <div key={i} className="flex items-start gap-3 px-3 py-2.5 rounded-lg border border-[#E5E7EB] hover:bg-[#F9FAFB]">
                          <FileText size={15} className="text-[#9CA3AF] flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] text-[#374151] truncate">{file.filename || file.name}</p>
                            {file.source_type === 'sharepoint' && (
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#EFF6FF] text-[#2563EB] mt-0.5 inline-block">SharePoint</span>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

          </div>
        )}

      </div>
    </div>
  )
}

export default SharedView
